import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const thyroidTesting: ContentBlock<"procedure"> = {
  id: makeId("proc-thyroid-testing"),
  kind: "procedure",
  tags: ["testing", "thyroid", "lab"],
  title: "Thyroid testing",
  subtitle:
    "Blood tests to evaluate thyroid function and its impact on fertility",
  body: {
    procedureName: "Thyroid testing",
    category: "diagnostic",
    description:
      "Blood tests measuring TSH, free T3, free T4, and thyroid antibodies to evaluate thyroid function. Thyroid disorders can significantly affect ovulation, implantation, and pregnancy outcomes.",
    howItWorks:
      "A blood sample is drawn from a vein in your arm, ideally in the morning. The lab measures thyroid-stimulating hormone (TSH) as the primary marker. If TSH is abnormal, free T3 and free T4 levels are checked to determine whether the thyroid is overactive or underactive. Thyroid antibodies (TPO and thyroglobulin antibodies) may also be tested to identify autoimmune thyroid disease, which is more common in women with fertility challenges.",
    duration: "5–10 minutes for the blood draw",
    anesthesia: "none",
    whatToExpect: [
      "A quick blood draw, same as any routine lab work",
      "No fasting is typically required, but confirm with your clinic",
      "Results are usually available within 1–3 business days",
      "If levels are abnormal, your doctor may prescribe thyroid medication before starting fertility treatment",
      "Thyroid levels are monitored throughout pregnancy if an issue is found",
    ],
    results: [
      {
        label: "When you hear",
        detail:
          "Thyroid labs usually return within 1-3 business days, depending on the lab.",
      },
      {
        label: "What you learn",
        detail:
          "TSH is the main screening value. Free T4, free T3, and thyroid antibodies may be added if your doctor needs more detail.",
      },
      {
        label: "Follow-up",
        detail:
          "If levels are outside your clinic's target range, medication or dose adjustment may be recommended before or during treatment.",
      },
    ],
    prepInstructions: [
      "Schedule the blood draw in the morning for the most accurate TSH reading",
      "Inform your clinic about any thyroid medications or biotin supplements you take",
      "No fasting is usually required, but check with your clinic",
    ],
    risks: [
      "Minor bruising or soreness at the needle site",
      "Very rare: lightheadedness during the blood draw",
    ],
    forWhom: "her",
    frBody: {
      procedureName: "Bilan thyroïdien",
      description:
        "Bilan sanguin dosant la TSH, la T3 libre, la T4 libre et les anticorps thyroïdiens pour évaluer la fonction thyroïdienne. Les troubles thyroïdiens peuvent avoir un impact significatif sur l'ovulation, l'implantation et l'évolution de la grossesse.",
      howItWorks:
        "Une prise de sang est réalisée au bras, idéalement le matin. Le laboratoire dose la TSH comme marqueur principal. Si la TSH est anormale, la T3 libre et la T4 libre sont contrôlées pour déterminer si la thyroïde est hyperactive ou hypoactive. Les anticorps thyroïdiens (anti-TPO et anti-thyroglobuline) peuvent également être recherchés pour identifier une thyroïdite auto-immune, plus fréquente chez les femmes présentant des difficultés de fertilité.",
      duration: "5 à 10 minutes pour la prise de sang",
      whatToExpect: [
        "Prise de sang rapide, comme un examen biologique de routine",
        "Aucun jeûne n'est généralement nécessaire ; vérifiez auprès de votre centre",
        "Résultats généralement disponibles sous 1 à 3 jours ouvrés",
        "En cas de résultat anormal, votre médecin peut prescrire un traitement avant de débuter l'AMP",
        "Les taux thyroïdiens sont surveillés pendant toute la grossesse en cas d'anomalie",
      ],
      results: [
        {
          label: "Quand vous avez des nouvelles",
          detail:
            "Les bilans thyroïdiens reviennent généralement sous 1 à 3 jours ouvrés, selon le laboratoire.",
        },
        {
          label: "Ce que vous apprenez",
          detail:
            "La TSH est la valeur principale. T4 libre, T3 libre et anticorps thyroïdiens peuvent être ajoutés si votre médecin veut plus de détail.",
        },
        {
          label: "Suivi",
          detail:
            "Si les taux sont hors de la cible de votre centre, un traitement ou ajustement de dose peut être recommandé avant ou pendant le parcours.",
        },
      ],
      prepInstructions: [
        "Programmez la prise de sang le matin pour un dosage de TSH plus fiable",
        "Signalez à votre centre tout traitement thyroïdien ou complément en biotine",
        "Un jeûne n'est généralement pas nécessaire, mais vérifiez auprès de votre centre",
      ],
      risks: [
        "Léger hématome ou sensibilité au point de ponction",
        "Très rare : sensation de malaise pendant la prise de sang",
      ],
    },
  },
  sources: [
    {
      title:
        "2017 Guidelines of the American Thyroid Association for the Diagnosis and Management of Thyroid Disease During Pregnancy and the Postpartum",
      authors: "Alexander EK, Pearce EN, Brent GA, et al.",
      journal: "Thyroid",
      year: 2017,
      url: "https://pubmed.ncbi.nlm.nih.gov/28056690/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(thyroidTesting);
